import React, { useContext } from "react";
import { AppSumoContext } from "contexts/AppSumoContext";

/**
 * Branded "product · tier" chips for AppSumo plans. A user may hold BOTH an
 * AppSumo license ("Studio", tier 1–3) and redeemed codes ("Followr", tier
 * 1–10); this shows one chip per owned product. Renders null if neither.
 *   tone   : 'onGradient' | 'onDark' | 'onLight'  (default 'onDark')
 *   layout : 'pills' | 'rows'                      (default 'pills')
 */
const PRODUCTS = {
    studio:  { name: "Studio",  dotDark: "bg-fuchsia-400", dotLight: "bg-fuchsia-500 dark:bg-fuchsia-400", fillLight: "bg-fuchsia-500/10 text-fuchsia-700 dark:bg-fuchsia-400/10 dark:text-fuchsia-300" },
    followr: { name: "Followr", dotDark: "bg-amber-400",   dotLight: "bg-amber-500 dark:bg-amber-400",     fillLight: "bg-amber-500/15 text-amber-700 dark:bg-amber-400/10 dark:text-amber-300" },
};

const ProductTierChips = ({ tone = "onDark", layout = "pills", className = "" }) => {
    const { isLicensee, getLicenseTier, getTier } = useContext(AppSumoContext);
    const onLight = tone === "onLight";

    const chips = [];
    if (isLicensee())  chips.push({ ...PRODUCTS.studio,  tier: getLicenseTier() });
    if (getTier() > 0) chips.push({ ...PRODUCTS.followr, tier: getTier() });
    if (chips.length === 0) return null;

    if (layout === "rows") {
        // ● Name .......... Tier N  (sidebar)
        return (
            <div className={`flex flex-col gap-1 w-full ${className}`.trim()}>
                {chips.map((c) => (
                    <div key={c.name} className="flex items-center justify-between gap-2">
                        <span className="flex items-center gap-1.5 min-w-0">
                            <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${onLight ? c.dotLight : c.dotDark}`} />
                            <span className={`text-xs font-semibold truncate ${onLight ? "text-gray-700 dark:text-gray-200" : "text-white/90"}`}>{c.name}</span>
                        </span>
                        <span className={`text-xs font-bold shrink-0 ${onLight ? "text-gray-900 dark:text-gray-100" : "text-white"}`}>Tier {c.tier}</span>
                    </div>
                ))}
            </div>
        );
    }

    // pills (default): tinted fills on light, glass pills on dark/gradient
    return (
        <div className={`flex flex-wrap items-center gap-2 ${className}`.trim()}>
            {chips.map((c) => onLight ? (
                <span key={c.name} className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold ${c.fillLight}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${c.dotLight}`} />
                    {c.name} · Tier {c.tier}
                </span>
            ) : (
                <span key={c.name} className="inline-flex items-center gap-2 pl-2.5 pr-3 py-1.5 rounded-full bg-white/10 border border-white/20 backdrop-blur-sm">
                    <span className={`w-1.5 h-1.5 rounded-full ${c.dotDark}`} />
                    <span className="text-xs font-semibold text-white/95">{c.name}</span>
                    <span className="text-xs font-bold text-white">Tier {c.tier}</span>
                </span>
            ))}
        </div>
    );
};

export default ProductTierChips;
